import React, { useEffect, useState } from "react";
import Head from "next/head";
import Layout from "../../Layout";
import CatalogHero from "../../components/CatalogHero/CatalogHero";
import { GetStaticPaths, GetStaticProps } from "next";
import { axiosServer } from "../../axios/axiosCustom";
import { API, API_SSR } from "../../api/api";
import {
  ICategories,
  ICategoryItem,
  ICategoryItemMain,
} from "../../interfaces/categories.interface";
import { ICategory } from "../../interfaces/categoryProducts.interface";
import { useGetProductsQuery } from "../../store/api/productsApi";
import { useAppSelector } from "../../store/store";
import { returnTrue } from "../../helpers/returnTrue";

const Products = ({ products, categories, activeCategory }: PageProps) => {
  const category = useAppSelector((state) => state.productsReducer.categories);
  const tags = useAppSelector((state) => state.productsReducer.tags);
  const page = useAppSelector((state) => state.productsReducer.page);
  const pageSize = useAppSelector((state) => state.productsReducer.pageSize);

  const { data: productsQuery, isFetching } = useGetProductsQuery({
    category: category.length ? category : [activeCategory.payload.id],
    tags,
    page,
    pageSize,
  });
  const { data: countProducts } = useGetProductsQuery({
    category: category.length ? category : [activeCategory.payload.id],
    tags,
    page: 1,
    pageSize: 50,
  });

  return (
    <>
      <Head>
        <title>
          {returnTrue(
            activeCategory.meta.meta_tags.title,
            activeCategory.payload.title
          )}
        </title>
        <meta
          name="description"
          content={returnTrue(
            activeCategory.meta.meta_tags.description,
            activeCategory.payload.global_settings.default_description
          )}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={returnTrue(
            activeCategory.meta.meta_tags.title,
            activeCategory.payload.title
          )}
        />
        <meta
          property="og:description"
          content={returnTrue(
            activeCategory.meta.meta_tags.description,
            activeCategory.payload.global_settings.default_description
          )}
        />
      </Head>
      <Layout globalSettings={activeCategory.payload.global_settings}>
        <CatalogHero
          isLoading={isFetching}
          countProducts={countProducts ? countProducts : products}
          categories={categories}
          products={productsQuery ? productsQuery : products}
        />
      </Layout>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data: categories } = await axiosServer.get<ICategories>(
    API_SSR.getCategoriesPage
  );

  return {
    paths: categories.data.map((e) => `/products/${e.slug}`),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  try {
    if (!params) {
      return {
        notFound: true,
        revalidate: 60,
      };
    }
    const { data: categories } = await axiosServer.get<ICategories>(
      API_SSR.getCategoriesPage
    );
    const { data: activeCategory } = await axiosServer.get<ICategoryItemMain>(
      API_SSR.getCategoriesPage + "/" + params.slug
    );
    const { data: currentCategoryProducts } = await axiosServer.get<ICategory>(
      API_SSR.getProductsByCategory(
        `categories[0]=${activeCategory.payload.id}&per_page=1000`
      )
    );

    if (!categories || !currentCategoryProducts) {
      return {
        notFound: true,
        revalidate: 60,
      };
    }
    return {
      props: {
        categories: categories,
        products: currentCategoryProducts,
        activeCategory: activeCategory,
      },
      revalidate: 60,
    };
  } catch (e) {
    console.log(e);
    return {
      notFound: true,
      revalidate: 60,
    };
  }
};

interface PageProps {
  categories: ICategories;
  products: ICategory;
  activeCategory: ICategoryItemMain;
}

export default Products;
